import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen } from 'lucide-react';
import PublicPageShell from '../components/PublicPageShell';
import { useLanguage } from '../contexts/LanguageContext';
import { getLocalizedPublicResources, resourcePath } from '../data/contentLibrary';

const resourcesPageCopy = {
  en: {
    h1: 'Practical guides for service-business websites',
    intro:
      'These editorial resources explain website structure, SEO, GEO, AEO, booking journeys and redesign decisions. Webouse keeps this section focused: no automated blog feed, no fake case results and no keyword-stuffed doorway pages.',
  },
  tr: {
    h1: 'Hizmet işletmeleri için pratik web sitesi rehberleri',
    intro:
      'Bu kaynaklar web sitesi yapısını, SEO, GEO, AEO, randevu yolculukları ve redesign kararlarını sade biçimde açıklar. Webouse bu bölümü odaklı tutar: otomatik blog akışı, sahte sonuç iddiaları veya anahtar kelimeyle şişirilmiş doorway sayfaları yoktur.',
  },
};

const ResourcesPage = () => {
  const { currentLang, t } = useLanguage();
  const copy = resourcesPageCopy[currentLang] || resourcesPageCopy.en;
  const resources = getLocalizedPublicResources(currentLang);

  return (
    <PublicPageShell mainClassName="pt-20">
      <section className="relative overflow-hidden bg-black">
        <div className="hero-grid absolute inset-0 pointer-events-none" />
        <div className="relative mx-auto max-w-[1180px] px-5 py-16 md:px-[7.6923%] md:py-24">
          <nav className="mb-8 flex flex-wrap gap-2 text-sm text-white/44" aria-label={t('common.breadcrumb')}>
            <Link to="/" className="hover:text-[#00FFD1]">{t('nav.home')}</Link>
            <span>/</span>
            <span className="text-white/70">{t('nav.resources')}</span>
          </nav>

          <div className="max-w-4xl">
            <p className="mb-4 text-sm font-semibold uppercase text-[#00FFD1]">
              {t('common.resourcesEyebrow')}
            </p>
            <h1 className="text-4xl font-semibold leading-tight text-white md:text-6xl">
              {copy.h1}
            </h1>
            <p className="mt-6 text-base leading-8 text-white/64 md:text-lg">
              {copy.intro}
            </p>
          </div>

          <div className="mt-12 grid gap-4">
            {resources.map((resource) => (
              <Link
                key={resource.slug}
                to={resourcePath(resource.slug)}
                className="group rounded-2xl border border-white/[0.08] bg-white/[0.03] p-6 transition-all hover:-translate-y-1 hover:border-[#00FFD1]/35 hover:bg-[#00FFD1]/[0.035]"
              >
                <div className="flex flex-col gap-5 md:flex-row md:items-start md:justify-between">
                  <div className="max-w-3xl">
                    <BookOpen className="mb-4 h-6 w-6 text-[#00FFD1]" />
                    <h2 className="text-2xl font-semibold text-white">{resource.title}</h2>
                    <p className="mt-3 text-sm leading-6 text-white/58">{resource.excerpt}</p>
                    <p className="mt-5 text-xs uppercase text-white/40">
                      {resource.author} - {t('common.updated')} {resource.lastUpdated}
                    </p>
                  </div>
                  <ArrowRight className="h-5 w-5 flex-none text-white/35 transition-transform group-hover:translate-x-1 group-hover:text-[#00FFD1]" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </PublicPageShell>
  );
};

export default ResourcesPage;
